import { useQuery } from "@tanstack/react-query";
import { useLocale } from "@genius-tracker/i18n/react";
import type { ReactNode } from "react";

import { useSeriesMailDelivery } from "../../../api/series.js";
import {
  fetchSvcDeliveryWatchdog,
  SVC_DELIVERY_WATCHDOG_KEY,
  type GtccStatus,
} from "../../../api/svc.js";
import { ChartPanel, GridMain, Row, TableWrap } from "../../../ui/primitives.js";

function tone(status: GtccStatus): "ok" | "warn" | "crit" | "info" {
  if (status === "healthy") return "ok";
  if (status === "warning" || status === "stale") return "warn";
  if (status === "critical") return "crit";
  return "info";
}

const routingSeverities = ["critical", "warning", "weekly", "monthly", "test"] as const;

export function DeliveryTab(): ReactNode {
  const { t } = useLocale();
  const mailDelivery = useSeriesMailDelivery();
  const deliveryWatchdog = useQuery({
    queryKey: SVC_DELIVERY_WATCHDOG_KEY,
    queryFn: ({ signal }) => fetchSvcDeliveryWatchdog(signal),
  });
  const data = mailDelivery.data?.ok ? mailDelivery.data.data : undefined;
  const watchdog = deliveryWatchdog.data?.ok ? deliveryWatchdog.data.data : undefined;
  const values =
    data?.points?.flatMap((point) =>
      point.deliveredPercent === null ? [] : [point.deliveredPercent],
    ) ?? [];
  const ceiling = Math.max(...values, 100);
  const points = values
    .map((value, index) => {
      const x = values.length === 1 ? 150 : (index * 300) / (values.length - 1);
      return `${x},${82 - (value / ceiling) * 64}`;
    })
    .join(" ");
  const unavailable =
    mailDelivery.data === undefined
      ? t("admin.gtcc.cockpit.trends.loading")
      : !mailDelivery.data.ok || data?.status === "unmeasurable"
        ? t("admin.gtcc.cockpit.trends.not_measured")
        : undefined;
  const measured = (value: string | number | null): string =>
    value === null ? t("admin.gtcc.cockpit.trends.not_measured") : String(value);
  const routing = watchdog?.routing ?? [];
  const routingBySeverity = new Map(routing.map((row) => [row.severity, row]));

  return (
    <div data-testid="gtcc-cockpit-delivery-tab">
      <GridMain>
        <ChartPanel
          title={t("admin.gtcc.cockpit.delivery.mail.title")}
          badge={<span className="mono muted">30 Tage</span>}
        >
          <Row tone="info" value={measured(watchdog?.mailDelivered7d ?? null)}>
            {t("admin.gtcc.cockpit.delivery.mail.delivered")}
          </Row>
          <Row tone="info" value={measured(watchdog?.bounces7d ?? null)}>
            {t("admin.gtcc.cockpit.delivery.mail.bounces")}
          </Row>
          <Row tone="info" value={measured(watchdog?.spf ?? null)}>
            {t("admin.gtcc.cockpit.delivery.mail.spf")}
          </Row>
          <Row tone="info" value={measured(watchdog?.dkim ?? null)}>
            {t("admin.gtcc.cockpit.delivery.mail.dkim")}
          </Row>
          <Row tone="info" value={measured(watchdog?.dmarc ?? null)}>
            {t("admin.gtcc.cockpit.delivery.mail.dmarc")}
          </Row>
          <div className="legend">
            <span>
              <b style={{ background: "var(--gt-sys-success)" }} />
              {t("admin.gtcc.cockpit.delivery.mail.rate")}
            </span>
          </div>
          {unavailable ? (
            <p className="sub">{unavailable}</p>
          ) : (
            <svg
              aria-label={t("admin.gtcc.cockpit.delivery.mail.rate")}
              viewBox="0 0 300 90"
              preserveAspectRatio="none"
              className="spark"
            >
              <polyline
                points={points}
                fill="none"
                stroke="var(--gt-sys-success)"
                strokeWidth="2"
              />
            </svg>
          )}
        </ChartPanel>
        <ChartPanel title={t("admin.gtcc.cockpit.delivery.watchdog.title")}>
          {(watchdog?.rows ?? [])
            .filter((row) => row.key !== "mail")
            .map((row) => (
              <Row key={row.key} tone={tone(row.status)} value={row.valueText}>
                {row.label}
              </Row>
            ))}
          <Row tone="info" value={measured(watchdog?.syntheticTestAlert?.schedule ?? null)}>
            {t("admin.gtcc.cockpit.delivery.watchdog.synthetic")}
          </Row>
          <Row tone="info" value={measured(watchdog?.reviews?.weekly ?? null)}>
            {t("admin.gtcc.cockpit.delivery.watchdog.weekly")}
          </Row>
          <Row tone="info" value={measured(watchdog?.reviews?.monthly ?? null)}>
            {t("admin.gtcc.cockpit.delivery.watchdog.monthly")}
          </Row>
        </ChartPanel>
      </GridMain>
      <GridMain>
        <ChartPanel title={t("admin.gtcc.cockpit.delivery.routing.title")}>
          <TableWrap>
            <thead>
              <tr>
                <th>SCHWEREGRAD</th>
                <th>TELEGRAM</th>
                <th>E-MAIL</th>
                <th>SPRACHE</th>
              </tr>
            </thead>
            <tbody>
              {routingSeverities.map((severity) => {
                const row = routingBySeverity.get(severity);
                const unavailableValue = t("admin.gtcc.cockpit.trends.not_measured");
                return (
                  <tr key={severity}>
                    <td>{t(`admin.gtcc.cockpit.delivery.routing.${severity}`)}</td>
                    <td className="mono">{row?.telegram ?? unavailableValue}</td>
                    <td className="mono">{row?.email ?? unavailableValue}</td>
                    <td className="mono">{row?.language ?? unavailableValue}</td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        </ChartPanel>
        <ChartPanel title={t("admin.gtcc.cockpit.delivery.recent.title")}>
          <Row tone="info" value={t("admin.gtcc.cockpit.trends.not_measured")}>
            {t("admin.gtcc.cockpit.delivery.recent.summary")}
          </Row>
          <p className="sub">{t("admin.gtcc.cockpit.delivery.recent.note")}</p>
        </ChartPanel>
      </GridMain>
    </div>
  );
}
